import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { TraceInfoItem, TraceItem } from '../../../../../infrastructure/OMSClient';
import { useOMS } from '../../../../../apps/hooks/oms/useOMS';
import { DeliveryTrace } from './Trace';

export default function DeliveryTracePage() {
  const { id } = useParams<{ id: string }>();
  const { trackingNo } = useParams<{ trackingNo: string }>();
  const navigate = useNavigate();
  const [traceInfo, setTraceInfo] = useState<TraceInfoItem | null>(null);
  const [traceItems, setTraceItems] = useState<TraceItem[]>([]);
  const [loading, setLoading] = useState(true);

  const oms = useOMS();

  useEffect(() => {
    const fetchTrackingHistory = async () => {
      if (!id || !trackingNo) {
        return;
      }
      try {
        const traceResponse = await oms.getDeliveryTrace(id);
        const matchingTraceInfo = traceResponse?.traceInfoList.find(
          (info) => info.trackingNo === trackingNo || info.mainTrackingNo === trackingNo,
        );
        setTraceInfo(matchingTraceInfo || null);
        setTraceItems(matchingTraceInfo?.traceList || []);
      } catch (error) {
        console.error('Error fetching tracking history:', error);
        setTraceInfo(null);
        setTraceItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTrackingHistory();
  }, [id, trackingNo]);

  if (!trackingNo) {
    return (
      <div className="p-4">
        <Button variant="outline-primary" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Order
        </Button>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">Invalid Tracking Number</h1>
        </div>
        <div className="text-center p-8">
          <h3 className="text-lg font-medium mb-2">No Tracking Number Provided</h3>
          <p className="text-muted-foreground">
            Please go back to the order details and click on a valid tracking number.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-2">
        <div className="flex items-center justify-between mb-4">
          <p className="text-2xl font-bold">Loading tracking information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2">
      <Button variant="outline-primary" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Order
      </Button>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Tracking Details</h1>
      </div>
      {traceInfo && traceItems && traceItems.length > 0 ? (
        <DeliveryTrace trackingNo={trackingNo} trackingHistory={traceItems} orderId={id || ''} />
      ) : (
        <div className="text-center p-8">
          <h3 className="text-lg font-medium mb-2">No Tracking History Available</h3>
          <p className="text-muted-foreground">
            Tracking information for this package is not yet available. Please check back later.
          </p>
        </div>
      )}
    </div>
  );
}
